import { WorkerTypes as DefaultWorkerTypes } from "@src/appV2/redesign/utils/WorkerTypes";
import { type LicenseData } from "@src/interface";
import { uniq } from "lodash";

export const WorkerStages = {
  ONBOARDING: "ONBOARDING",
  ENROLLED: "ENROLLED",
  PROBATION: "PROBATION",
  RESTRICTED: "RESTRICTED",
  DEACTIVATED: "DEACTIVATED",
  DELETION_REQUESTED: "DELETION_REQUESTED",
  SOFT_DELETED: "SOFT_DELETED",
};

export type WorkerStage = keyof typeof WorkerStages;

export function isWorkerStageEnum(value: string): value is WorkerStage {
  return Object.values(WorkerStages).includes(value as WorkerStage);
}

export function isWorkerActive(stage: string): boolean {
  return [WorkerStages.ENROLLED, WorkerStages.ONBOARDING, WorkerStages.RESTRICTED].includes(
    stage as WorkerStage
  );
}

export const getWorkerStageLabel = (stage: WorkerStage) => {
  if (stage === WorkerStages.SOFT_DELETED) {
    return "Deleted";
  }

  return stage;
};

export const WorkerSubstages = {
  LIMITED: "LIMITED",
  WARNING: "WARNING",
  INVITE_REQUIRED: "INVITE_REQUIRED",
};

type FailedWorkerStages = typeof WorkerStages.PROBATION;

const WORKER_STAGES_REASONS_ENUM: Record<FailedWorkerStages, Record<string, string>> = {
  [WorkerStages.PROBATION]: {
    ATTENDANCE_AND_TIMELINE: "ATTENDANCE_AND_TIMELINE",
    PROFESSIONAL_ETIQUETTE: "PROFESSIONAL_ETIQUETTE",
    CLINICAL_HEALTHCARE_TRAINING: "CLINICAL_HEALTHCARE_TRAINING",
    BEDSIDE_MANNER: "BEDSIDE_MANNER",
    OTHER: "OTHER",
    NCNS: "NCNS",
  },
  [WorkerStages.RESTRICTED]: {
    ATTENDANCE_ISSUES: "ATTENDANCE_ISSUES",
    EXCESSIVE_DNRS: "EXCESSIVE_DNRS",
    OTHER: "OTHER",
  },
  [WorkerSubstages.INVITE_REQUIRED]: {
    ATTENDANCE_ISSUES: "ATTENDANCE_ISSUES",
    PERFORMANCE_ISSUES: "PERFORMANCE_ISSUES",
    SAFETY_INCIDENT: "SAFETY_INCIDENT",
    OTHER: "OTHER",
  },
  [WorkerStages.DEACTIVATED]: {
    ATTENDANCE_ISSUES: "ATTENDANCE_ISSUES",
    EXCESSIVE_DNRS: "EXCESSIVE_DNRS",
    FAILED_BACKGROUND_CHECK: "FAILED_BACKGROUND_CHECK",
    FRAUD_SIGN_UP: "FRAUD_SIGN_UP",
    HCP_CHOSE_TO_DEACTIVATE: "HCP_CHOSE_TO_DEACTIVATE",
    MAJOR_INCIDENT: "MAJOR_INCIDENT",
    MINOR_INCIDENT: "MINOR_INCIDENT",
    TIMESHEET_FRAUD: "TIMESHEET_FRAUD",
    DUPLICATE_SIGNUP: "DUPLICATE_SIGNUP",
  },
};

export const WORKER_STAGES_REASONS = {
  [WorkerStages.PROBATION]: Object.values(WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION]),
  [WorkerStages.RESTRICTED]: Object.values(WORKER_STAGES_REASONS_ENUM[WorkerStages.RESTRICTED]),
  [WorkerSubstages.INVITE_REQUIRED]: Object.values(
    WORKER_STAGES_REASONS_ENUM[WorkerSubstages.INVITE_REQUIRED]
  ),
  [WorkerStages.DEACTIVATED]: Object.values(WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED]),
};

export const WORKER_STAGES_REASONS_DATA = {
  [WorkerStages.PROBATION]: {
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].ATTENDANCE_AND_TIMELINE]: {
      description: "Attendance & Timeliness Issues",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].PROFESSIONAL_ETIQUETTE]: {
      description: "Professional Etiquette Needs Improvement",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].CLINICAL_HEALTHCARE_TRAINING]: {
      description: "Clinical & Healthcare Training Needs Improvement",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].BEDSIDE_MANNER]: {
      description: "Bedside Manner Needs Improvement",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].OTHER]: {
      description: "Other Complaints, please explain",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.PROBATION].NCNS]: {
      description: "No show",
      mandatoryNote: false,
    },
  },
  [WorkerStages.RESTRICTED]: {
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.RESTRICTED].ATTENDANCE_ISSUES]: {
      description: "Attendance Issues",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.RESTRICTED].EXCESSIVE_DNRS]: {
      description: "Excessive DNRs (Not Safety Related)",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.RESTRICTED].OTHER]: {
      description: "Other",
      mandatoryNote: true,
    },
  },
  [WorkerSubstages.INVITE_REQUIRED]: {
    [WORKER_STAGES_REASONS_ENUM[WorkerSubstages.INVITE_REQUIRED].ATTENDANCE_ISSUES]: {
      description: "Attendance Issues",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerSubstages.INVITE_REQUIRED].PERFORMANCE_ISSUES]: {
      description: "Performance Issues",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerSubstages.INVITE_REQUIRED].SAFETY_INCIDENT]: {
      description: "Safety Incident",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerSubstages.INVITE_REQUIRED].OTHER]: {
      description: "Other",
      mandatoryNote: true,
    },
  },
  [WorkerStages.DEACTIVATED]: {
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].ATTENDANCE_ISSUES]: {
      description: "Attendance issues",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].EXCESSIVE_DNRS]: {
      description: "Excessive Do-Not-Return requests",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].FAILED_BACKGROUND_CHECK]: {
      description: "Failed background check",
      mandatoryNote: false,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].FRAUD_SIGN_UP]: {
      description: "Fraud sign up",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].HCP_CHOSE_TO_DEACTIVATE]: {
      description: "HCP chose to deactivate",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].MAJOR_INCIDENT]: {
      description: "Major incident",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].MINOR_INCIDENT]: {
      description: "Minor incident",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].TIMESHEET_FRAUD]: {
      description: "Timesheet fraud",
      mandatoryNote: true,
    },
    [WORKER_STAGES_REASONS_ENUM[WorkerStages.DEACTIVATED].DUPLICATE_SIGNUP]: {
      description: "Duplicate Signup",
      mandatoryNote: true,
    },
  },
};

interface LimitedWorkerSubstage {
  name: typeof WorkerSubstages.LIMITED;
  accessMetadata: {
    scoreThreshold: number;
    basketSize: number;
    leadTimeThresholdInHrs: number;
  };
}

interface WarningWorkerSubstage {
  name: typeof WorkerSubstages.WARNING;
  accessMetadata: {
    scoreThreshold: number;
    basketSize: number;
    leadTimeThresholdInHrs: number;
    failRestrictionDurationInDays: number;
  };
}

export interface InviteRequiredWorkerSubstage {
  name: typeof WorkerSubstages.INVITE_REQUIRED;
}

export type WorkerSubstage =
  | LimitedWorkerSubstage
  | WarningWorkerSubstage
  | InviteRequiredWorkerSubstage;

const LVN_STATES = new Set(["California", "Texas"]);

export const getWorkerShiftTypeLabel = (shiftType?: string, state?: string): string => {
  if (!shiftType) {
    return "";
  }

  if (shiftType === DefaultWorkerTypes.LVN) {
    if (state && !LVN_STATES.has(state)) {
      return "LPN";
    }

    return state ? "LVN" : "LVN/LPN";
  }

  return shiftType.toUpperCase().replaceAll("_", " ");
};

export const getWorkerQualifications = (licensesData: LicenseData[] | undefined) => {
  if (!licensesData) {
    return [];
  }

  const qualifications: string[] = [];
  for (const data of licensesData) {
    qualifications.push(data.qualification);
  }

  return uniq(qualifications.sort());
};

export const workerPreferences = {
  QUALIFICATION: "qualification",
  DISTANCE: "distance",
};
